import { PieChart } from "echarts/charts";
import { GraphicComponent } from "echarts/components";
import * as echarts from "echarts/core";
import type { ReactNode } from "react";
import { useMemo } from "react";

import type { RiskTierCounts } from "@busright/data-contract";
import { COLORS, EChart, type EChartOption, RAG } from "@busright/ui";

import { formatInt } from "@/lib/format";

// The shared EChart base only registers Bar/Line. The donut needs Pie + the centered Graphic
// label, so register them here (idempotent) before this component first renders.
echarts.use([PieChart, GraphicComponent]);

export interface RiskDonutProps {
  readonly counts: RiskTierCounts;
  readonly height?: number;
}

// OK/WARN/CRIT composition donut. RAG colors only (this IS a health metric). Center shows the
// scored total; a custom legend with counts sits below.
export const RiskDonut = ({ counts, height = 240 }: RiskDonutProps): ReactNode => {
  const total = counts.ok + counts.warn + counts.critical;

  const option = useMemo<EChartOption>(
    () => ({
      tooltip: {
        trigger: "item",
        backgroundColor: COLORS.surface2,
        borderColor: COLORS.border,
        textStyle: { color: COLORS.textPrimary },
        formatter: "{b}: {c} ({d}%)",
      },
      series: [
        {
          type: "pie",
          radius: ["62%", "84%"],
          center: ["50%", "50%"],
          avoidLabelOverlap: false,
          label: { show: false },
          labelLine: { show: false },
          itemStyle: { borderColor: COLORS.surface1, borderWidth: 2 },
          data: [
            { value: counts.ok, name: "OK", itemStyle: { color: RAG.OK } },
            { value: counts.warn, name: "WARN", itemStyle: { color: RAG.WARN } },
            { value: counts.critical, name: "CRITICAL", itemStyle: { color: RAG.CRITICAL } },
          ],
        },
      ],
      graphic: {
        type: "group",
        left: "center",
        top: "center",
        children: [
          {
            type: "text",
            style: {
              text: formatInt(total),
              fill: COLORS.textPrimary,
              font: "600 26px ui-monospace, monospace",
              textAlign: "center",
            },
            top: -14,
          },
          {
            type: "text",
            style: {
              text: "scored",
              fill: COLORS.textMuted,
              font: "11px sans-serif",
              textAlign: "center",
            },
            top: 16,
          },
        ],
      },
    }),
    [counts, total],
  );

  return (
    <div>
      <EChart option={option} height={height} />
      <div className="risk-legend">
        <span className="risk-legend-item">
          <span className="swatch" style={{ background: RAG.OK }} /> OK{" "}
          <span className="count">{formatInt(counts.ok)}</span>
        </span>
        <span className="risk-legend-item">
          <span className="swatch" style={{ background: RAG.WARN }} /> WARN{" "}
          <span className="count">{formatInt(counts.warn)}</span>
        </span>
        <span className="risk-legend-item">
          <span className="swatch" style={{ background: RAG.CRITICAL }} /> CRIT{" "}
          <span className="count">{formatInt(counts.critical)}</span>
        </span>
      </div>
    </div>
  );
};
